import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StopIcon from "@mui/icons-material/Stop";
import TerminalIcon from "@mui/icons-material/Terminal";
import { getImportStatusChipClass } from "./utils/catalogStatusDisplay";
import { CM_TOOLTIPS } from "./utils/tooltipsContent";

export default function CatalogImportLogDialog({
  open,
  onClose,
  onClearLogs,
  onStopImport,
  stoppingImport = false,
  logLines,
  logContainerRef,
  jobStatus,
  importRunning,
  title = "Log import cataloghi",
}) {
  const processed = jobStatus?.processed ?? 0;
  const total = jobStatus?.total ?? 0;
  const vendorProgress = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const stepProgress = jobStatus?.current_step_percent ?? 0;
  const stepLabel = jobStatus?.current_step_label || "In attesa";
  const currentVendor = jobStatus?.current_vendor_name
    || (jobStatus?.current_vendor_id ? `Vendor ${jobStatus.current_vendor_id}` : null);
  const showActivity = importRunning || stoppingImport || jobStatus?.cancel_requested;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ className: "cm-dialog" }}
    >
      <DialogTitle className="cm-dialog-title">
        <Stack direction="row" spacing={1} alignItems="center" className="cm-dialog-title-row">
          <TerminalIcon fontSize="small" />
          <span>{title}</span>
          {jobStatus?.status && (
            <Chip size="small" className={getImportStatusChipClass(jobStatus.status)} label={jobStatus.status} />
          )}
        </Stack>
        <IconButton
          aria-label="Chiudi log"
          onClick={onClose}
          className="cm-dialog-close"
          size="small"
          title={CM_TOOLTIPS.chiudiDialog}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="cm-dialog-body" dividers>
        {showActivity && (
          <Box className="cm-dialog-progress">
            <Stack spacing={1.5}>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography className="cm-muted-text" variant="body2">
                    Vendor: {processed}/{total}
                  </Typography>
                  <Typography variant="body2">{vendorProgress}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={vendorProgress} />
              </Box>

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography className="cm-muted-text" variant="body2">
                    Step corrente: {stepLabel}
                  </Typography>
                  <Typography variant="body2">{stepProgress}%</Typography>
                </Stack>
                <LinearProgress
                  variant={stepProgress > 0 ? "determinate" : "indeterminate"}
                  value={stepProgress}
                />
              </Box>

              {currentVendor && (
                <Typography className="cm-muted-text" variant="caption">
                  In lavorazione: {currentVendor}
                </Typography>
              )}

              {(stoppingImport || jobStatus?.cancel_requested) && (
                <Typography className="cm-muted-text" variant="caption">
                  Arresto in corso
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        <Box
          ref={logContainerRef}
          component="pre"
          className="cm-log-viewer"
        >
          {logLines.length > 0
            ? logLines.join("\n")
            : showActivity
              ? "In attesa dei log..."
              : "Nessun log disponibile."}
        </Box>
      </DialogContent>

      <DialogActions className="cm-dialog-actions">
        {onStopImport && importRunning && (
          <Button
            onClick={() => {
              void onStopImport();
            }}
            disabled={stoppingImport || jobStatus?.cancel_requested}
            startIcon={<StopIcon />}
            title={CM_TOOLTIPS.stopImport}
          >
            {stoppingImport || jobStatus?.cancel_requested ? "Arresto..." : "Arresta import"}
          </Button>
        )}
        {onClearLogs && logLines.length > 0 && (
          <Button onClick={onClearLogs} disabled={importRunning} title={CM_TOOLTIPS.svuotaLog}>
            Svuota log
          </Button>
        )}
        <Button onClick={onClose} variant="contained" title={CM_TOOLTIPS.chiudiLog}>
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CatalogImportLogDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onClearLogs: PropTypes.func,
  onStopImport: PropTypes.func,
  stoppingImport: PropTypes.bool,
  logLines: PropTypes.arrayOf(PropTypes.string).isRequired,
  logContainerRef: PropTypes.object,
  jobStatus: PropTypes.object,
  importRunning: PropTypes.bool,
  title: PropTypes.string,
};
